import { StyleSheet, View, ScrollView, Platform } from 'react-native';
import { Text, Button, Surface, useTheme, List, Switch, Portal, Dialog } from 'react-native-paper';
import { StatusBar } from 'expo-status-bar';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useOnboarding } from '@/src/context/OnboardingContext';
import { MaterialIcons } from '@expo/vector-icons';
import * as Notifications from 'expo-notifications';
import * as Location from 'expo-location';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LoadingScreen } from '@/src/components/LoadingScreen';

export const buildPermissionSnapshot = (
  notificationsEnabled: boolean,
  locationForegroundEnabled: boolean,
  locationBackgroundEnabled: boolean,
) => ({
  notifications: notificationsEnabled,
  locationForeground: locationForegroundEnabled,
  locationBackground: locationBackgroundEnabled,
});

export default function PermissionsScreen() {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const { state, setPermission, completeOnboarding } = useOnboarding();
  
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    state.permissions.notifications
  );
  const [locationForegroundEnabled, setLocationForegroundEnabled] = useState(
    state.permissions.locationForeground
  );
  const [locationBackgroundEnabled, setLocationBackgroundEnabled] = useState(
    state.permissions.locationBackground
  );
  
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);

  const toggleNotifications = async () => {
    if (Platform.OS === 'web') {
      setNotificationsEnabled(false);
      setPermission('notifications', false);
      return;
    }

    if (!notificationsEnabled) {
      const { status } = await Notifications.requestPermissionsAsync();
      
      if (status === 'granted') {
        setNotificationsEnabled(true);
        setPermission('notifications', true);
      } else {
        showPermissionDialog(
          t('notificationsPermission', 'Notificações'),
          t('notificationsPermissionMessage', 'Para receber notificações, você precisa habilitar esta permissão nas configurações do seu dispositivo.')
        );
      }
    } else {
      setNotificationsEnabled(false);
      setPermission('notifications', false);
    }
  };

  const toggleLocationForeground = async () => {
    if (Platform.OS === 'web') {
      setLocationForegroundEnabled(true);
      setPermission('locationForeground', true);
      return;
    }

    if (!locationForegroundEnabled) {
      const { status } = await Location.requestForegroundPermissionsAsync();
      
      if (status === 'granted') {
        setLocationForegroundEnabled(true);
        setPermission('locationForeground', true);
      } else {
        showPermissionDialog(
          t('locationPermission', 'Localização'),
          t('locationPermissionMessage', 'Para usar sua localização atual, você precisa habilitar esta permissão nas configurações do seu dispositivo.')
        );
      }
    } else {
      setLocationForegroundEnabled(false);
      setPermission('locationForeground', false);
      
      // If foreground is disabled, also disable background
      if (locationBackgroundEnabled) {
        setLocationBackgroundEnabled(false);
        setPermission('locationBackground', false);
      }
    }
  };

  const toggleLocationBackground = async () => {
    if (Platform.OS === 'web') {
      setLocationBackgroundEnabled(false);
      setPermission('locationBackground', false);
      return;
    }

    // Can't enable background without foreground
    if (!locationForegroundEnabled) {
      showPermissionDialog(
        t('locationBackgroundError', 'Permissão necessária'),
        t('locationBackgroundErrorMessage', 'Você precisa habilitar a localização em primeiro plano antes de habilitar em segundo plano.')
      );
      return;
    }
    
    if (!locationBackgroundEnabled) {
      const { status } = await Location.requestBackgroundPermissionsAsync();

      if (status === 'granted') {
        setLocationBackgroundEnabled(true);
        setPermission('locationBackground', true);
      } else {
        showPermissionDialog(
          t('backgroundLocationPermission', 'Localização em segundo plano'),
          Platform.OS === 'ios'
            ? t('backgroundLocationPermissionMessageIos', 'Para usar sua localização em segundo plano, habilite esta permissão nas configurações do dispositivo e selecione "Sempre" em vez de "Durante o uso do app".')
            : t('backgroundLocationPermissionMessage', 'Para usar sua localização em segundo plano, você precisa habilitar esta permissão nas configurações do seu dispositivo. É importante selecionar a opção "Permitir sempre" para garantir o funcionamento correto do aplicativo.')
        );
      }
    } else {
      setLocationBackgroundEnabled(false);
      setPermission('locationBackground', false);
    }
  };

  const showPermissionDialog = (title: string, message: string) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setShowDialog(true);
  };

  const handleFinish = async () => {
    setIsCompleting(true);
    
    // Mark device as initialized
    await AsyncStorage.setItem('deviceInitialized', 'true');
    
    // Complete onboarding with the latest local controls. React state updates
    // from the toggles are asynchronous, so the context snapshot may still be
    // one render behind at this point.
    await completeOnboarding(buildPermissionSnapshot(
      notificationsEnabled,
      locationForegroundEnabled,
      locationBackgroundEnabled,
    ));
    
    // Redirect to dashboard
    router.replace('/dashboard/home');
  };

  // Show loading screen while completing onboarding
  if (isCompleting) {
    return <LoadingScreen message={t('completingOnboarding', 'Finalizando onboarding...')} />;
  }

  return (
    <Surface style={styles.container}>
      <StatusBar style="auto" />
      
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.iconContainer}>
          <MaterialIcons name="security" size={80} color={theme.colors.primary} />
        </View>
        
        <Text style={[styles.title, { color: theme.colors.primary }]}>
          {t('permissionsTitle', 'Permissões do Aplicativo')}
        </Text>
        
        <Text style={styles.description}>
          {t('permissionsDescription', 'Para oferecer a melhor experiência, precisamos das seguintes permissões:')}
        </Text>
        
        <List.Section style={styles.listSection}>
          {Platform.OS !== 'web' && (
            <List.Item
              title={t('notificationsPermissionTitle', 'Notificações')}
              description={t('notificationsPermissionDescription', 'Para receber alertas e informações importantes')}
              left={props => <MaterialIcons {...props} name="notifications" size={24} color={theme.colors.primary} />}
              right={props => (
                <Switch
                  value={notificationsEnabled}
                  onValueChange={toggleNotifications}
                  color={theme.colors.primary}
                />
              )}
            />
          )}
          
          <List.Item
            title={t('locationForegroundPermissionTitle', 'Localização (Em uso)')}
            description={t('locationForegroundPermissionDescription', 'Para mostrar sua localização atual no mapa')}
            left={props => <MaterialIcons {...props} name="location-on" size={24} color={theme.colors.primary} />}
            right={props => (
              <Switch
                value={locationForegroundEnabled}
                onValueChange={toggleLocationForeground}
                color={theme.colors.primary}
              />
            )}
          />
          
          {Platform.OS !== 'web' && (
            <>
              <List.Item
                title={t('locationBackgroundPermissionTitle', 'Localização (Segundo plano)')}
                description={t('locationBackgroundPermissionDescription', 'Para continuar rastreando mesmo quando o app estiver fechado')}
                left={props => <MaterialIcons {...props} name="location-searching" size={24} color={theme.colors.primary} />}
                right={props => (
                  <Switch
                    value={locationBackgroundEnabled}
                    onValueChange={toggleLocationBackground}
                    color={theme.colors.primary}
                    disabled={!locationForegroundEnabled}
                  />
                )}
              />
              <Text style={[styles.importantText, { color: theme.colors.onSurfaceVariant }]}>
                {t('locationBackgroundPermissionImportant')}
              </Text>
            </>
          )}
        </List.Section>
        
        <View style={styles.stepsContainer}>
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.primary }]} />
        </View>
        
        <Button
          mode="contained"
          style={styles.button}
          contentStyle={styles.buttonContent}
          onPress={handleFinish}
          loading={isCompleting}
          disabled={isCompleting}
        >
          {t('finish', 'Concluir')}
        </Button>
      </ScrollView>
      
      <Portal>
        <Dialog visible={showDialog} onDismiss={() => setShowDialog(false)} style={Platform.OS === 'web' ? {
          maxWidth: 500,
          width: '90%',
          alignSelf: 'center',
        } : {}}>
          <Dialog.Title>{dialogTitle}</Dialog.Title>
          <Dialog.Content>
            <Text>{dialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDialog(false)}>OK</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
  },
  iconContainer: {
    alignItems: 'center',
    marginVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  listSection: {
    marginBottom: 24,
  },
  stepsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stepIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginHorizontal: 4,
  },
  button: {
    marginTop: 16,
  },
  buttonContent: {
    height: 48,
  },
  importantText: {
    fontSize: 14,
    fontStyle: 'italic',
    marginHorizontal: 16,
    marginBottom: 16,
    lineHeight: 20,
  },
}); 
